import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Users, CreditCard, AlertTriangle, Scale, Mail } from 'lucide-react';

const TermsOfServicePage = () => {
  const navigate = useNavigate();
  
  // Import Google Font Caveat
  React.useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const sections = [
    {
      icon: <FileText className="w-6 h-6 text-teal-500" />,
      title: "1. Objet du service",
      content: `Dis Maman! est une application d'assistance parentale utilisant l'intelligence artificielle pour :
      
      • Répondre aux questions des enfants de manière adaptée à leur âge
      • Générer des histoires personnalisées et illustrées
      • Proposer un contenu éducatif et divertissant sécurisé
      
      Le service est destiné à être utilisé par les parents avec leurs enfants, sous supervision parentale recommandée pour les plus jeunes.`
    },
    {
      icon: <Users className="w-6 h-6 text-teal-500" />,
      title: "2. Conditions d'utilisation",
      content: `En utilisant Dis Maman!, vous acceptez de :
      
      • Fournir des informations exactes lors de l'inscription
      • Utiliser le service de manière responsable et légale
      • Ne pas tenter de contourner les filtres de sécurité
      • Superviser l'utilisation par vos enfants
      • Respecter les droits de propriété intellectuelle
      
      Vous devez être majeur (18 ans minimum) pour créer un compte.`
    },
    {
      icon: <CreditCard className="w-6 h-6 text-teal-500" />,
      title: "3. Abonnements et paiements",
      content: `**Essai gratuit** : 7 jours d'accès complet sans engagement.
      
      **Abonnements** :
      • Les abonnements sont facturés mensuellement ou annuellement
      • Le renouvellement est automatique sauf annulation
      • Vous pouvez annuler à tout moment depuis votre compte
      • Le remboursement n'est pas possible pour les périodes entamées
      
      **Packs d'histoires** :
      • Achat unique, valable sans expiration
      • Non remboursables une fois achetés
      
      Les paiements sont sécurisés via Stripe.`
    },
    {
      icon: <AlertTriangle className="w-6 h-6 text-amber-500" />,
      title: "4. Limitations de responsabilité",
      content: `Dis Maman! s'efforce de fournir un contenu de qualité, mais :
      
      • Le contenu généré par IA peut parfois contenir des inexactitudes
      • Le service ne remplace pas l'éducation parentale
      • Nous ne garantissons pas une disponibilité 100% du service
      • Les conseils fournis sont à titre informatif uniquement
      
      En cas de contenu inapproprié, signalez-le immédiatement à contact@dismaman.com`
    },
    {
      icon: <Scale className="w-6 h-6 text-teal-500" />,
      title: "5. Propriété intellectuelle",
      content: `**Contenu de l'application** :
      L'application, son design, son code et ses fonctionnalités sont la propriété de Dis Maman!.
      
      **Histoires générées** :
      Les histoires créées pour vous sont votre propriété. Vous pouvez les conserver, imprimer et partager librement.
      
      **Illustrations** :
      Les illustrations sont générées par IA et vous sont concédées pour usage personnel.`
    }
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 hover:bg-gray-100 rounded-full transition-colors mr-4"
            data-testid="terms-back-btn"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: '"Caveat", cursive' }}>
            Conditions d'utilisation
          </h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Introduction */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-lg" data-testid="terms-intro-section">
          <div className="flex items-center mb-4">
            <FileText className="w-8 h-8 text-teal-500 mr-3" />
            <h2 className="text-xl font-bold text-gray-800">Conditions Générales d'Utilisation</h2>
          </div>
          <p className="text-gray-600">
            En utilisant l'application Dis Maman!, vous acceptez les présentes conditions générales d'utilisation (CGU). 
            Veuillez les lire attentivement avant d'utiliser le service.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Dernière mise à jour : Décembre 2024
          </p>
        </div>

        {/* Sections */}
        {sections.map((section, index) => (
          <div 
            key={index} 
            className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-6 shadow-lg"
            data-testid={`terms-section-${index}`}
          >
            <div className="flex items-center mb-4">
              {section.icon}
              <h2 className="text-xl font-bold text-gray-800 ml-3">{section.title}</h2>
            </div>
            <div className="text-gray-600 whitespace-pre-line">
              {section.content.split('**').map((part, i) => 
                i % 2 === 0 ? part : <strong key={i}>{part}</strong>
              )}
            </div>
          </div>
        ))}

        {/* Additional sections */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-6 shadow-lg" data-testid="terms-modifications-section">
          <h2 className="text-xl font-bold text-gray-800 mb-4">6. Modifications des CGU</h2>
          <p className="text-gray-600">
            Nous nous réservons le droit de modifier ces conditions à tout moment. 
            Les modifications importantes vous seront notifiées par email. 
            L'utilisation continue du service après modification vaut acceptation des nouvelles conditions.
          </p>
        </div>

        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-lg" data-testid="terms-law-section">
          <h2 className="text-xl font-bold text-gray-800 mb-4">7. Droit applicable</h2>
          <p className="text-gray-600">
            Les présentes CGU sont régies par le droit français. 
            En cas de litige, les tribunaux français seront seuls compétents.
          </p>
        </div>

        {/* Contact */}
        <div className="bg-gradient-to-r from-blue-500 to-teal-500 rounded-2xl p-6 text-white shadow-lg" data-testid="terms-contact-section">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Mail className="w-6 h-6 mr-2" />
            Besoin d'éclaircissements ?
          </h2>
          <p className="mb-4">
            Si vous avez des questions concernant ces conditions d'utilisation, 
            notre équipe est là pour vous aider.
          </p>
          <a 
            href="mailto:contact@dismaman.com"
            className="inline-flex items-center px-6 py-3 bg-white text-teal-600 rounded-xl font-medium hover:bg-gray-100 transition-colors"
          >
            <Mail className="w-5 h-5 mr-2" />
            contact@dismaman.com
          </a>
        </div>

        {/* Footer info */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>Dis Maman! - Application éditée en France</p>
          <p>© 2024 Dis Maman! - Tous droits réservés</p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
